import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { X, Check, AlertTriangle, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

gsap.registerPlugin(ScrollTrigger);

const ComparisonSection = () => {
  const sectionRef = useRef(null);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.comparison-left',
        { opacity: 0, x: -100 },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%'
          }
        }
      );

      gsap.fromTo('.comparison-right',
        { opacity: 0, x: 100 },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, [language]);

  return (
    <section ref={sectionRef} className="py-20 lg:py-32 bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.comparison.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.comparison.title} <span className="text-red-600 dark:text-red-500">{t.comparison.highlight}</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.comparison.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="comparison-left">
            <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg border border-red-100 dark:border-red-900/30 transition-colors duration-300 h-full">
              <div className="bg-gradient-to-r from-red-100 to-red-50 dark:from-red-900/40 dark:to-red-900/20 p-6 border-b border-red-100 dark:border-red-900/30">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-7 h-7 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {t.comparison.kiosk.title}
                    </h3>
                    <p className="text-red-600 dark:text-red-400 font-medium" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {language === 'en' ? 'Challenges Faced' : 'येणाऱ्या अडचणी'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {t.comparison.kiosk.items.map((issue, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-red-50/50 dark:bg-red-900/10 rounded-xl transition-colors duration-300">
                    <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center flex-shrink-0">
                      <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </div>
                    <p className="text-gray-700 dark:text-gray-200 font-medium" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {issue}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="comparison-right">
            <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg border border-green-100 dark:border-green-900/30 transition-colors duration-300 h-full">
              <div className="bg-gradient-to-r from-green-100 to-green-50 dark:from-green-900/40 dark:to-green-900/20 p-6 border-b border-green-100 dark:border-green-900/30">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center">
                    <ShieldCheck className="w-7 h-7 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {t.comparison.store.title}
                    </h3>
                    <p className="text-green-600 dark:text-green-400 font-medium" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {language === 'en' ? 'Current Advantages' : 'सध्याचे फायदे'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {t.comparison.store.items.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-green-50/50 dark:bg-green-900/10 rounded-xl transition-colors duration-300">
                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <p className="text-gray-700 dark:text-gray-200 font-medium" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {benefit}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-full px-6 py-3">
            <span className="text-2xl">💡</span>
            <p className="text-gray-800 dark:text-white font-medium" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {language === 'en' ? 'Evolution is key to sustainable growth' : 'शाश्वत वाढीसाठी बदल हीच गुरुकिल्ली आहे'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
